import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Menu, X, Sun, Moon, Phone } from 'lucide-react';
import { useTheme } from '@/App';
import BookingModal from '@/components/BookingModal';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const { currentTheme, toggleTheme, canToggle } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'About', href: '#about' },
    { label: 'Expertise', href: '#expertise' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Experience', href: '#experience' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'py-3' : 'py-5'
        }`}
      >
        <div className={`mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl transition-all duration-300 ${
          isScrolled ? 'glass-strong rounded-2xl shadow-lg mx-4' : ''
        }`}>
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <motion.a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center cursor-pointer relative group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Show logo based on theme */}
              <img 
                src={currentTheme === 'light' ? '/logo-dark.png' : '/logo.png'}
                alt="Mustakim Ahmed Logo" 
                className="h-9 w-auto relative z-10"
                style={currentTheme === 'dark' ? {
                  filter: 'brightness(1.1) drop-shadow(0 0 10px hsl(350, 90%, 55%))'
                } : {}}
              />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <motion.button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="text-base font-bold text-muted-foreground hover:text-foreground transition-colors"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  {item.label}
                </motion.button>
              ))}
              
              {/* Theme Toggle Button - Only shows when theme is 'optional' */}
              {canToggle && (
                <motion.button
                  onClick={toggleTheme}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {currentTheme === 'dark' ? (
                    <Moon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Sun className="w-4 h-4 text-yellow-500" />
                  )}
                </motion.button>
              )}

              <Button
                variant="gradient"
                size="sm"
                onClick={() => setIsBookingModalOpen(true)}
                className="flex items-center gap-2"
                data-testid="lets-talk-btn"
              >
                <Phone className="w-4 h-4" />
                Let's Talk
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              {/* Theme Toggle for Mobile */}
              {canToggle && (
                <button
                  onClick={toggleTheme}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center"
                >
                  {currentTheme === 'dark' ? (
                    <Moon className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Sun className="w-4 h-4 text-yellow-500" />
                  )}
                </button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence mode="sync">
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="fixed inset-0 z-40 pt-24 px-4 md:hidden"
          >
            <div className="glass-strong rounded-2xl p-6 shadow-xl">
              <div className="flex flex-col gap-4">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.href}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.1 }}
                    onClick={() => scrollToSection(item.href)}
                    className="text-lg font-bold text-foreground text-left py-2 hover:text-primary transition-colors"
                  >
                    {item.label}
                  </motion.button>
                ))}
                <Button
                  variant="gradient"
                  size="lg"
                  className="mt-4"
                  onClick={() => { setIsMobileMenuOpen(false); setIsBookingModalOpen(true); }}
                >
                  Let's Talk
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </>
  );
};

export { Navigation };
